import Button from '@risksmart-app/components/src/button';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import { memoize } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_COL_WIDTH } from 'src/App.config';
import { Permission } from 'src/rbac/Permission';

import Link from '@/components/link';
import type { ResolvedRating } from '@/ratings/useRiskRatingResolver';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';
import type {
  StatefulTableOptions,
  UseGetTablePropsOptions,
} from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetStatelessTableProps } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import { exportStyleFromValue } from '@/utils/table/pdfExportStyles';
import type {
  FieldConfig,
  TableFields,
  TablePropsWithActions,
} from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';
import { addEnterpriseRiskUrl, enterpriseRiskDetailsUrl } from '@/utils/urls';

import type {
  EnterpriseRiskFields,
  EnterpriseRiskRegisterFields,
} from './types';
import {
  assessedDimValue,
  compareDimValues,
  exportDimValue,
  resolveDimRating,
  useLabelledFields,
} from './useLabelledFields';

/**
 * Badge for an aggregated likelihood/impact dimension: `Unlikely (2.5)`. A
 * median or worst-case dimension carries its chosen child's own value, so it
 * can be the unassessed `0` sentinel — which reads as `-`, never `Unrated (0)`.
 */
const dimCell = (
  value: number | null | undefined,
  locale: string,
  resolve: (value: number) => ResolvedRating | undefined
) => {
  const assessed = assessedDimValue(value);

  return assessed == null ? (
    '-'
  ) : (
    <SimpleRatingBadge rating={resolveDimRating(assessed, locale, resolve)} />
  );
};

type EnterpriseRiskScore = NonNullable<EnterpriseRiskFields['score']>;

/** The single-value likelihood/impact fields of the score (not the arrays). */
type DimField = {
  [K in keyof EnterpriseRiskScore]-?: EnterpriseRiskScore[K] extends
    | null
    | number
    | undefined
    ? K
    : never;
}[keyof EnterpriseRiskScore];

/**
 * Sorts a dimension column on its score rather than on the labelled cell text
 * its key would otherwise sort by. Memoised per field because the table
 * recognises the active sort column by comparator identity across renders.
 */
const sortByDim = memoize(
  (
    field: DimField
  ): Pick<FieldConfig<EnterpriseRiskRegisterFields>, 'sortingComparator'> => ({
    sortingComparator: (a, b) =>
      compareDimValues(a.score?.[field], b.score?.[field]),
  })
);

/**
 * Export twins of a dimension column. CSV gets the bare score, so the column
 * is unambiguous and sorts numerically in a spreadsheet; the PDF, being read
 * rather than parsed, gets the same `Unlikely (2.5)` text as the cell.
 */
const exportDim = (
  field: DimField,
  locale: string,
  resolve: (value: number) => ResolvedRating | undefined
): Pick<
  FieldConfig<EnterpriseRiskRegisterFields>,
  'exportVal' | 'pdfExportVal'
> => ({
  exportVal: (item) => exportDimValue(item.score?.[field]),
  pdfExportVal: (item) => {
    const assessed = assessedDimValue(item.score?.[field]);

    return assessed == null
      ? '-'
      : resolveDimRating(assessed, locale, resolve).label;
  },
});

export const useGetFieldConfig =
  (): TableFields<EnterpriseRiskRegisterFields> => {
    const { t, i18n } = useTranslation(['common'], { keyPrefix: 'columns' });
    const locale = i18n.language;
    const { t: st } = useTranslation(['common'], {
      keyPrefix: 'risks.columns',
    });
    const { t: et } = useTranslation(['common'], {
      keyPrefix: 'enterpriseRisks.columns',
    });
    // Ratings render from the STORED rating values (resolved from
    // likelihood × impact by the aggregation since RSP-6195) — not from a
    // range lookup on the score, which custom risk matrices never match.
    const { getByValue: getResidualRating } = useRating('risk_controlled');
    const { getByValue: getInherentRating } = useRating('risk_uncontrolled');
    const { resolveLikelihood, resolveImpact } = useRiskRatingResolver(
      'standard',
      { configType: 'risk_assessment' }
    );

    return {
      Id: {
        header: t('guid'),
      },
      SequentialIdLabelled: {
        header: t('id'),
        sortingField: 'SequentialId',
      },
      Title: {
        header: t('title'),
        cell: (item) => (
          <Link variant={'secondary'} href={enterpriseRiskDetailsUrl(item.Id)}>
            {item.Title}
          </Link>
        ),
      },
      ParentTitle: {
        header: st('parent_risk'),
      },
      TierLabelled: {
        header: st('risk_tier'),
      },
      TreatmentLabelled: {
        header: st('risk_treatment'),
      },
      Description: {
        header: st('risk_description'),
        maxWidth: MAX_COL_WIDTH,
      },
      InherentMeanLabelled: {
        header: et('inherentRatingMean'),
        cell: (item) => (
          <SimpleRatingBadge
            rating={getInherentRating(item.score?.InherentRatingMean)}
          />
        ),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentRatingMean,
          getInherentRating
        ),
      },
      InherentWorstCaseLabelled: {
        header: et('inherentRatingWorstCase'),
        cell: (item) => (
          <SimpleRatingBadge
            rating={getInherentRating(item.score?.InherentRatingWorstCase)}
          />
        ),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentRatingWorstCase,
          getInherentRating
        ),
      },
      ResidualMeanLabelled: {
        header: et('residualRatingMean'),
        cell: (item) => (
          <SimpleRatingBadge
            rating={getResidualRating(item.score?.ResidualRatingMean)}
          />
        ),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualRatingMean,
          getResidualRating
        ),
      },
      ResidualWorstCaseLabelled: {
        header: et('residualRatingWorstCase'),
        cell: (item) => (
          <SimpleRatingBadge
            rating={getResidualRating(item.score?.ResidualRatingWorstCase)}
          />
        ),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualRatingWorstCase,
          getResidualRating
        ),
      },
      InherentLikelihoodMeanLabelled: {
        header: et('inherentLikelihoodMean'),
        cell: (item) =>
          dimCell(
            item.score?.InherentLikelihoodMean,
            locale,
            resolveLikelihood
          ),
        ...sortByDim('InherentLikelihoodMean'),
        ...exportDim('InherentLikelihoodMean', locale, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentLikelihoodMean,
          resolveLikelihood
        ),
      },
      InherentImpactMeanLabelled: {
        header: et('inherentImpactMean'),
        cell: (item) =>
          dimCell(item.score?.InherentImpactMean, locale, resolveImpact),
        ...sortByDim('InherentImpactMean'),
        ...exportDim('InherentImpactMean', locale, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentImpactMean,
          resolveImpact
        ),
      },
      ResidualLikelihoodMeanLabelled: {
        header: et('residualLikelihoodMean'),
        cell: (item) =>
          dimCell(
            item.score?.ResidualLikelihoodMean,
            locale,
            resolveLikelihood
          ),
        ...sortByDim('ResidualLikelihoodMean'),
        ...exportDim('ResidualLikelihoodMean', locale, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualLikelihoodMean,
          resolveLikelihood
        ),
      },
      ResidualImpactMeanLabelled: {
        header: et('residualImpactMean'),
        cell: (item) =>
          dimCell(item.score?.ResidualImpactMean, locale, resolveImpact),
        ...sortByDim('ResidualImpactMean'),
        ...exportDim('ResidualImpactMean', locale, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualImpactMean,
          resolveImpact
        ),
      },
      InherentLikelihoodMedianLabelled: {
        header: et('inherentLikelihoodMedian'),
        cell: (item) =>
          dimCell(
            item.score?.InherentLikelihoodMedian,
            locale,
            resolveLikelihood
          ),
        ...sortByDim('InherentLikelihoodMedian'),
        ...exportDim('InherentLikelihoodMedian', locale, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentLikelihoodMedian,
          resolveLikelihood
        ),
      },
      InherentImpactMedianLabelled: {
        header: et('inherentImpactMedian'),
        cell: (item) =>
          dimCell(item.score?.InherentImpactMedian, locale, resolveImpact),
        ...sortByDim('InherentImpactMedian'),
        ...exportDim('InherentImpactMedian', locale, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentImpactMedian,
          resolveImpact
        ),
      },
      ResidualLikelihoodMedianLabelled: {
        header: et('residualLikelihoodMedian'),
        cell: (item) =>
          dimCell(
            item.score?.ResidualLikelihoodMedian,
            locale,
            resolveLikelihood
          ),
        ...sortByDim('ResidualLikelihoodMedian'),
        ...exportDim('ResidualLikelihoodMedian', locale, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualLikelihoodMedian,
          resolveLikelihood
        ),
      },
      ResidualImpactMedianLabelled: {
        header: et('residualImpactMedian'),
        cell: (item) =>
          dimCell(item.score?.ResidualImpactMedian, locale, resolveImpact),
        ...sortByDim('ResidualImpactMedian'),
        ...exportDim('ResidualImpactMedian', locale, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualImpactMedian,
          resolveImpact
        ),
      },
      InherentLikelihoodWorstCaseLabelled: {
        header: et('inherentLikelihoodWorstCase'),
        cell: (item) =>
          dimCell(
            item.score?.InherentLikelihoodWorstCase,
            locale,
            resolveLikelihood
          ),
        ...sortByDim('InherentLikelihoodWorstCase'),
        ...exportDim('InherentLikelihoodWorstCase', locale, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentLikelihoodWorstCase,
          resolveLikelihood
        ),
      },
      InherentImpactWorstCaseLabelled: {
        header: et('inherentImpactWorstCase'),
        cell: (item) =>
          dimCell(item.score?.InherentImpactWorstCase, locale, resolveImpact),
        ...sortByDim('InherentImpactWorstCase'),
        ...exportDim('InherentImpactWorstCase', locale, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentImpactWorstCase,
          resolveImpact
        ),
      },
      ResidualLikelihoodWorstCaseLabelled: {
        header: et('residualLikelihoodWorstCase'),
        cell: (item) =>
          dimCell(
            item.score?.ResidualLikelihoodWorstCase,
            locale,
            resolveLikelihood
          ),
        ...sortByDim('ResidualLikelihoodWorstCase'),
        ...exportDim('ResidualLikelihoodWorstCase', locale, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualLikelihoodWorstCase,
          resolveLikelihood
        ),
      },
      ResidualImpactWorstCaseLabelled: {
        header: et('residualImpactWorstCase'),
        cell: (item) =>
          dimCell(item.score?.ResidualImpactWorstCase, locale, resolveImpact),
        ...sortByDim('ResidualImpactWorstCase'),
        ...exportDim('ResidualImpactWorstCase', locale, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualImpactWorstCase,
          resolveImpact
        ),
      },
      CreatedAtTimestamp: dateColumnFromConfig({
        header: { header: t('created_on') },
        dateField: 'CreatedAtTimestamp',
      }),
      ModifiedAtTimestamp: dateColumnFromConfig({
        header: { header: t('updated_on') },
        dateField: 'ModifiedAtTimestamp',
      }),
      CreatedByUser: {
        header: t('created_by_id'),
      },
      ModifiedByUser: {
        header: t('updated_by_id'),
      },
    };
  };

export const useGetEnterpriseRiskTableProps = (
  records: EnterpriseRiskFields[] | undefined
): UseGetTablePropsOptions<EnterpriseRiskRegisterFields> => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'enterpriseRisks',
  });
  const data = useLabelledFields(records);
  const fields = useGetFieldConfig();

  return useMemo(() => {
    return {
      tableId: 'enterpriseRiskRegister',
      data,
      fields,
      entityLabel: st('entity_name'),
      emptyCollectionAction: (
        <Permission permission={'insert:enterprise_risk'}>
          <Button href={addEnterpriseRiskUrl()}>{st('createNewButton')}</Button>
        </Permission>
      ),
      enableFiltering: true,
      initialColumns: ['Title', 'ParentTitle', 'TierLabelled'],
      preferencesStorageKey: 'EnterpriseRisksTable-Preferences',
      customAttributeFormIds: ['risk'],
    };
  }, [st, data, fields]);
};

export const useGetCollectionTableProps = (
  records: EnterpriseRiskFields[] | undefined
): TablePropsWithActions<EnterpriseRiskRegisterFields> => {
  const props = useGetEnterpriseRiskTableProps(records);

  return useGetTableProps(props);
};

export const useGetEnterpriseRiskSmartWidgetTableProps = (
  records: EnterpriseRiskFields[] | undefined,
  statefulTableOptions: StatefulTableOptions<EnterpriseRiskRegisterFields>
): TablePropsWithActions<EnterpriseRiskRegisterFields> => {
  const props = useGetEnterpriseRiskTableProps(records);

  return useGetStatelessTableProps<EnterpriseRiskRegisterFields>({
    ...props,
    ...statefulTableOptions,
    enableFiltering: false,
  });
};
